import { useState } from "react";
import { Link } from "react-router-dom";
import { Gavel } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { http, mediaUrl } from "@/lib/api";
import { useGroup } from "@/lib/GroupContext";
import { TapePlayer } from "@/components/TapePlayer";
import { VideoSlot } from "@/components/FootagePending";
import { Sigil } from "@/components/Sigil";
import { Breadcrumbs } from "@/components/Brand";
import { SporeTakeover } from "@/components/SporeTakeover";
import { CHARGE } from "@/lib/briefing";

const ORDER = ["vale", "ward", "marsh", "cale"];

function AccuseForm() {
  const { state, setState } = useGroup();
  const [pick, setPick] = useState(null);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const ok = pick && reason.trim().length >= 20;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/case/accuse", { suspect: pick, reason });
      setState(data);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || "The accusation could not be filed.");
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <div data-testid="accusation-form">
      <Breadcrumbs items={[["Suspect interviews", "/"], ["Charge decision"]]} />
      <span className="caption mt-8">Operation SPORE · Major Investigation Team</span>
      <h1 className="text-5xl font-black mt-2">{CHARGE.title}</h1>
      <p className="text-xl mt-4 max-w-3xl">{CHARGE.intro}</p>
      <fieldset className="mt-10">
        <legend className="text-3xl font-black mb-2">{CHARGE.question}</legend>
        <span className="form-hint mb-5">{CHARGE.select}</span>
        <div className="grid grid-cols-2 gap-x-10 gap-y-5 max-w-4xl mt-5" role="radiogroup">
          {ORDER.map((sid) => {
            const s = state.suspects[sid];
            const on = pick === sid;
            return (
              <button key={sid} type="button" role="radio" aria-checked={on} onClick={() => setPick(sid)}
                className="flex items-start gap-4 text-left p-2" data-testid={`accuse-option-${sid}`}>
                <span className={`radio-circle ${on ? "on" : ""}`} />
                <span className="pt-1">
                  <span className="block text-2xl font-bold">{s.name}</span>
                  <span className="block form-hint !mt-0">{s.role}</span>
                  {on && <span className="sr-only" data-testid={`accuse-option-${sid}-selected`}>Selected</span>}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <div className="mt-10 max-w-4xl">
        <label htmlFor="accusation-reason-input" className="form-label text-3xl">{CHARGE.grounds}</label>
        <span className="form-hint">{CHARGE.groundsHint} At least 20 characters.</span>
        <textarea id="accusation-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={5} maxLength={2000}
          className="form-input w-full mt-3" data-testid="accusation-reason-input" />
        <span className={`block mt-2 text-lg ${reason.trim().length >= 20 ? "text-[#00703c] font-bold" : "text-[#505a5f]"}`} data-testid="accusation-reason-count">
          {reason.trim().length}/20 characters minimum
        </span>
        <button className="btn-spore mt-8" disabled={!ok || busy} onClick={() => setConfirm(true)} data-testid="submit-accusation-button">
          <Gavel size={20} /> Submit charge decision
        </button>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-none border-2 border-[#0b0c0c] max-w-xl" data-testid="accusation-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-3xl font-black">Charge {pick && state.suspects[pick].name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-lg text-[#0b0c0c]">This is the only charge decision for this case. It cannot be changed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="btn-ghost h-auto" data-testid="accusation-confirm-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction className="btn-spore h-auto" onClick={submit} disabled={busy} data-testid="accusation-confirm-submit">Submit charge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Filed({ v }) {
  return (
    <dl className="panel p-6 mt-8 max-w-4xl grid grid-cols-[220px_1fr] gap-y-3 text-lg" data-testid="saved-verdict">
      <dt className="font-bold">Charged</dt>
      <dd data-testid="saved-verdict-suspect">{v.suspect_name}</dd>
      <dt className="font-bold">Grounds</dt>
      <dd className="italic" data-testid="saved-verdict-reason">"{v.reason}"</dd>
    </dl>
  );
}

function CaseClosed({ v }) {
  return (
    <div data-testid="case-closed-screen">
      <Breadcrumbs items={[["Suspect interviews", "/"], ["Charge decision"]]} />
      <div className="bg-[#00703c] text-white text-center px-10 py-12 mt-8 max-w-4xl rise">
        <h1 className="text-6xl font-black tracking-wide" data-testid="case-closed-title">CASE CLOSED</h1>
        <p className="text-2xl mt-5">You charged the killer of Dr Ebaro Monroe:</p>
        <p className="text-4xl font-black mt-2" data-testid="case-closed-killer">{v.finale.killer}</p>
      </div>
      <Filed v={v} />
      <div className="grid grid-cols-12 gap-8 mt-12">
        <div className="col-span-12 xl:col-span-6">
          <VideoSlot slot={v.arrest_video} label="Arrest footage" testid="arrest-video-slot" />
        </div>
      </div>
      <h2 className="text-3xl font-black mt-14 mb-6">Recovered C3 intercom</h2>
      <TapePlayer src={mediaUrl("/api/audio/intercom")} lines={v.finale.transcript} label="EXHIBIT · C3 INTERIOR INTERCOM" sublabel="Facility clock 21:08 – 21:14" variant="intercom" testid="intercom" />
    </div>
  );
}

function SporesReveal({ v }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="relative z-10" data-testid="spores-reveal-screen">
      <SporeTakeover />
      <div className="flex items-center gap-10 pt-12 rise">
        <div className="sigil-glow spore-breathe"><Sigil size={140} color="#010202" /></div>
        <div>
          <div className="label-mono">The network speaks</div>
          <h1 className="font-display text-7xl text-[#b6ffe6] tracking-[0.16em] mt-3 glow-text" data-testid="spores-reveal-title">THE SPORES REVEAL</h1>
          <p className="text-2xl text-[#cfeee5] mt-4 font-fell italic">You charged {v.suspect_name}. You chose wrong.</p>
        </div>
      </div>
      <Filed v={v} />
      <div className="mt-12">
        <TapePlayer src={mediaUrl("/api/audio/spores")} lines={v.finale.transcript} label="WHISPERS FROM BENEATH THE STONE" variant="spores" progressive={!revealed} onEnded={() => setRevealed(true)} testid="spores" />
      </div>
      <div className="mt-10">
        {revealed ? (
          <div className="panel p-10 text-center rise" data-testid="true-killer-reveal">
            <div className="label-mono">The true killer</div>
            <div className="font-display text-6xl text-white tracking-[0.12em] mt-4 glow-text" data-testid="true-killer-name">{v.finale.killer}</div>
            <p className="text-xl text-[#cfeee5] mt-4 font-fell italic">The Husk is Ebaro Monroe.</p>
          </div>
        ) : (
          <button className="btn-ghost" onClick={() => setRevealed(true)} data-testid="spores-reveal-now-button">Reveal the full truth now</button>
        )}
      </div>
    </div>
  );
}

export default function Accusation() {
  const { state } = useGroup();
  if (!state) return <p className="py-10 text-lg">Opening…</p>;
  if (state.verdict) return state.verdict.correct ? <CaseClosed v={state.verdict} /> : <SporesReveal v={state.verdict} />;
  if (!state.accusation_unlocked) {
    return (
      <div data-testid="accusation-locked">
        <Breadcrumbs items={[["Suspect interviews", "/"], ["Charge decision"]]} />
        <h1 className="text-5xl font-black mt-8">Charge decision</h1>
        <div className="warning-text mt-6 max-w-3xl">
          <span className="warning-icon" aria-hidden>!</span>
          <strong className="text-2xl" data-testid="accusation-locked-message">Unlock all four hidden clues to make your accusation ({state.clues_unlocked}/4)</strong>
        </div>
        <Link to="/" className="btn-spore mt-8" data-testid="accusation-back-to-hub">Back to suspect interviews</Link>
      </div>
    );
  }
  return <AccuseForm />;
}
